<template>
  <div
    class="datepicker__container"
    v-bind="$attrs"
  >
    <v-date-picker
      class="datepicker"
      v-model="date"
      color="indigo"
      :min-date="availableDates.start"
      :max-date="availableDates.end"
      :is-required="required"
      :select-attribute="availableDates.start"
      @dayclick="(_, event) => { event.target.blur() }"
      @popover-did-show="is_date_popover_open = true"
      @popover-did-hide="is_date_popover_open = false"
    >
      <template #default="{ togglePopover }">
        <div class="input__container input--border input--radius padding__lr--12">
          <button
            class="input__container"
            type="button"
            aria-haspopup="dialog"
            :aria-label="date ? t('literal.selected_date_x', { date: formatedDate }) : t('literal.select_a_date')"
            :aria-expanded="is_date_popover_open"
            @click="e => handleClickDate(e, togglePopover)"
          >
            <input
              type="date"
              class="input"
              :id="`${id}`"
              :class="{ 'is-placeholder': !date }"
              :min="minDate"
              :max="maxDate"
              :value="inputDate"
              :placeholder="t('literal.select_a_date')"
              :required="required"
              :aria-invalid="!!v?.$error"
              :aria-describedby="v?.$error ? `${id}_error` : undefined"
              @keydown.space.prevent="e => handleClickDate(e, togglePopover)"
              @blur="e => handleBlurDate('date', e.target)"
            >

            <fa-icon
              class="color--vlg input__icon"
              :icon="['fal', 'calendar']"
              fixed-width
            />
          </button>

          <transition name="fade">
            <button
              type="button"
              :aria-label="t('literal.clear_date')"
              @click.stop="date = null"
              v-if="!required && clearable && date"
            >
              <fa-icon
                class="link__color--vlg-black link--naked"
                :icon="['fal', 'xmark']"
                fixed-width
              />
            </button>
          </transition>
        </div>
      </template>
    </v-date-picker>

    <template v-if="showTime">
      <label
        :for="`${id}_time`"
        class="sr-only"
      >
        {{ t('literal.select_time') }}
      </label>

      <v-date-picker
        class="datepicker"
        v-model="date"
        color="indigo"
        mode="time"
        :available-dates="availableDates"
        :is-24hr="true"
        @dayclick="(_, event) => { event.target.blur() }"
        @popover-did-show="is_time_popover_open = true"
        @popover-did-hide="is_time_popover_open = false"
      >
        <template #default="{ togglePopover }">
          <div class="input__container input--border input--radius padding__lr--12">
            <button
              class="input__container"
              :aria-label="date ? t('literal.selected_time_x', { time: formatedTime }) : t('literal.select_time')"
              :aria-expanded="is_time_popover_open"
              @click="e => handleClickDate(e, togglePopover)"
            >
              <input
                class="input"
                type="time"
                :id="`${id}_time`"
                :class="{ 'is-placeholder': !date }"
                :value="inputTime"
                :required="required"
                :aria-invalid="!!v?.$error"
                :aria-describedby="v?.$error ? `${id}_error` : undefined"
                @keydown.space.prevent="e => handleClickDate(e, togglePopover)"
                @blur="e => handleBlurDate('time', e.target)"
              >

              <fa-icon
                class="color--vlg input__icon"
                :icon="['fal', 'clock']"
                fixed-width
              />
            </button>
          </div>
        </template>
      </v-date-picker>
    </template>
  </div>

  <p
    :id="`${id}_error`"
    class="input--errors input--error"
    role="alert"
    aria-live="polite"
    v-if="v?.$error"
  >
    <template v-if="v.required?.$invalid">
      {{ t('errors.input_datepicker_required') }}
    </template>

    <template v-else-if="v.minDate?.$invalid">
      {{ t('errors.input_datepicker_min_date', { value: format(v.minDate.$params.min, dateFormat) }) }}
    </template>

    <template v-else-if="v.maxDate?.$invalid">
      {{ t('errors.input_datepicker_max_date', { value: format(v.maxDate.$params.max, dateFormat) }) }}
    </template>
  </p>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from '@/vendors/i18n'
import { format } from 'date-fns'
import isMobile from 'is-mobile'
import { DatePicker as VDatePicker } from 'v-calendar'
import 'v-calendar/dist/style.css'

const model = defineModel({ type: [Date, String] })

const { availableDates, showTime, openDate, required } = defineProps({
  id: String,
  availableDates: { type: Object, default: () => ({}) },
  showTime: { type: Boolean, default: false },
  openDate: [Date, String],
  required: { type: Boolean, default: true },
  clearable: { type: Boolean, default: false },
  v: Object
})

const { t } = useI18n()

const isMobileDevice = isMobile({ tablet: true })

const is_date_popover_open = ref(false)
const is_time_popover_open = ref(false)

const dateFormat = computed(() => showTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy')
const minDate = computed(() => isMobileDevice && availableDates.start ? format(availableDates.start, 'yyyy-MM-dd') : '')
const maxDate = computed(() => isMobileDevice && availableDates.end ? format(availableDates.end, 'yyyy-MM-dd') : '')
const inputDate = computed(() => date.value ? format(date.value, 'yyyy-MM-dd') : '')
const inputTime = computed(() => date.value ? format(date.value, 'HH:mm') : '')

const date = computed({
  get: () => model.value ? new Date(model.value) : '',
  set: value => {
    if (value && !isNaN(value.getTime())) model.value = value.toISOString()
    else if (!required) model.value = null
  }
})

const currentDate = computed(() => {
  if (date.value) return date.value
  if (openDate) return new Date(openDate)
  if (availableDates.start) return new Date(availableDates.start)

  return new Date()
})

const formatedDate = computed(() => format(date.value, 'dd/MM/yyyy'))
const formatedTime = computed(() => format(date.value, 'HH:mm'))

const handleClickDate = (e, toggle) => {
  if (!date.value) date.value = currentDate.value

  if (!isMobileDevice) {
    e.preventDefault()

    toggle()
  }
}

const handleBlurDate = (type, target) => {
  const [year, month, day] = type === 'time' ? format(currentDate.value, 'yyyy-MM-dd').split('-') : target.value.split('-')
  const [hour, minute] = type === 'time' ? target.value.split(':') : format(currentDate.value, 'HH:mm').split(':')

  date.value = new Date(year, month - 1, day, hour, minute)
}
</script>

<style lang="scss" scoped>
.input {
  background: transparent;
  position: relative;
  height: 43px;
  flex-grow: 1;
  padding: 12px 0;

  &::-webkit-inner-spin-button,
  &::-webkit-calendar-picker-indicator {
    display: none;
    appearance: none;
  }

  &::-webkit-date-and-time-value {
    text-align: left;
  }

  &.is-placeholder {
    color: $vlg;
  }

  &__container {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px
  }

  &__icon {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    background: $white;
  }
}

.datepicker {
  flex-grow: 1;
  position: relative;

  &__container {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;

    @include mq(sm) {
      flex-direction: row;
    }
  }
}
</style>
